// Routines transport — carved from `src/features/workflows/client.ts`
// (ARCFE-1102 · D1), which is now a named re-export shim.
//
// History: `git log --follow -M30% -- packages/agents/src/data/api/routines.ts`.
// The default 50% rename threshold just misses the pair — this header dilutes
// the similarity below it — so the plain `--follow` reads as if the module were
// born here. It was not; it traces to #14338.
//
// Transport comes from the `@heidi/api` configured-client registry (E5,
// ARCFE-805) — the registry hands back the app's real `scribeHttpV2` singleton,
// same interceptors and same retry/refresh state, so request behaviour is
// byte-identical. `@/utils/scribeHTTP` was the ONLY blocker the census reported
// on this module at HEAD `efb1945c2f`, and this is that blocker's ratified
// heal: the same shape `packages/coding/src/data/api/session-codes-api.ts`
// (1.9), `session/documents` (1.14) and this package's own `api/inbox.ts` and
// `api/harness.ts` already ship.
//
// Bodies are otherwise identical to the pre-move source, `scribeHttpV2.` ->
// `http()`. The structural `ScribeHttp` view keeps axios types out of this
// package's public surface, and it types exactly the three config fields these
// 23 calls pass and nothing more:
//
//   `params`             — every list/read.
//   `paramsSerializer`   — axios's OBJECT form, because `getSchedulesBatch`
//                          passes `{ indexes: null }`. That is what serialises
//                          `workflow_ids: [a, b]` as a repeated
//                          `workflow_ids=a&workflow_ids=b` rather than
//                          `workflow_ids[0]=a`; retyping it as a function would
//                          silently change the wire format of the batch read.
//   `timeout`            — two calls override it, and both overrides are
//                          load-bearing (see `run` and `listAllRuns`).
//
// The object shape is preserved exactly, including method NAMES: `remove` is
// not `delete`, and `install`/`installForConnector` are distinct calls against
// two different `/workflow-presets` routes. Consumer suites mock this surface
// by module (`jest.mock('@/features/workflows/client', …)`), so a renamed
// member is a silent runtime failure at four call sites.
import { getScribeHttpClients } from '@heidi/api/client';

import type {
  AddEventRuleRequest,
  BatchEventRuleResponse,
  BatchScheduleResponse,
  CreateWorkflowRequest,
  EventRule,
  EventRuleListResponse,
  RunRoutineRequest,
  RunWorkflowResponse,
  SetScheduleRequest,
  ShareWorkflowRequest,
  UnshareWorkflowRequest,
  UpdateWorkflowRequest,
  Workflow,
  WorkflowListQuery,
  WorkflowListResponse,
  WorkflowRunListResponse,
  WorkflowSchedule,
  WorkflowTemplateListResponse,
  WorkspaceShareTarget,
} from '../../contracts/routines.types';

interface ScribeHttpConfig {
  params?: unknown;
  paramsSerializer?: { indexes: null | boolean };
  timeout?: number;
}

interface ScribeHttp {
  get<T>(url: string, config?: ScribeHttpConfig): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown, config?: ScribeHttpConfig): Promise<{ data: T }>;
  patch<T>(url: string, body?: unknown): Promise<{ data: T }>;
  put<T>(url: string, body?: unknown): Promise<{ data: T }>;
  delete<T>(url: string): Promise<{ data: T }>;
}

const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

const BASE = '/workflows';

export const routinesClient = {
  getWorkspaceShareTarget: (workspaceId: string) =>
    http().get<WorkspaceShareTarget>(`${BASE}/share-target`, {
      params: { workspace_id: workspaceId },
    }),

  list: (params: WorkflowListQuery = {}) =>
    http().get<WorkflowListResponse>(BASE, {
      params: { include_shared: true, ...params },
    }),

  get: (workflowId: string) => http().get<Workflow>(`${BASE}/${workflowId}`),

  create: (body: CreateWorkflowRequest) => http().post<Workflow>(BASE, body),

  update: (workflowId: string, body: UpdateWorkflowRequest) =>
    http().patch<Workflow>(`${BASE}/${workflowId}`, body),

  remove: (workflowId: string) => http().delete<void>(`${BASE}/${workflowId}`),

  share: (workflowId: string, body: ShareWorkflowRequest) =>
    http().post<Workflow>(`${BASE}/${workflowId}/share`, body),

  unshare: (workflowId: string, body: UnshareWorkflowRequest) =>
    http().post<Workflow>(`${BASE}/${workflowId}/unshare`, body),

  run: (workflowId: string, body: RunRoutineRequest = {}) =>
    // The kick-off normally answers in seconds (the run itself executes
    // async). Cap the wait so a wedged request surfaces as the run-failed
    // toast + an idle, retryable play — never an infinite spinner.
    http().post<RunWorkflowResponse>(`${BASE}/${workflowId}/run`, body, {
      timeout: 60_000,
    }),

  /** Fork a system workflow into a user-owned copy (setup-wizard save path). */
  fork: (workflowId: string) => http().post<Workflow>(`${BASE}/${workflowId}/fork`),

  // ── Preset templates ────────────────────────────────────────────

  /** The Heidi-curated preset catalog shown in the Marketplace gallery. */
  listTemplates: (params: { connector?: string } = {}) =>
    http().get<WorkflowTemplateListResponse>('/workflow-presets', {
      params,
    }),

  /** Install a preset: creates (or returns) the user's editable workflow. */
  install: (templateId: string) =>
    http().post<Workflow>(`/workflow-presets/${templateId}/install`),

  /** Idempotently install primary hidden routines for a newly linked app. */
  installForConnector: (connector: string) =>
    http().post<WorkflowListResponse>(
      `/workflow-presets/install-for-connector/${encodeURIComponent(connector)}`
    ),

  // ── Schedule ────────────────────────────────────────────────────

  getSchedule: (workflowId: string) =>
    http().get<WorkflowSchedule | null>(`${BASE}/${workflowId}/schedule`),

  getSchedulesBatch: (workflowIds: string[]) =>
    http().get<BatchScheduleResponse>(`${BASE}/schedules/batch`, {
      params: { workflow_ids: workflowIds },
      paramsSerializer: { indexes: null },
    }),

  setSchedule: (workflowId: string, body: SetScheduleRequest) =>
    http().put<WorkflowSchedule>(`${BASE}/${workflowId}/schedule`, body),

  deleteSchedule: (workflowId: string) =>
    http().delete<void>(`${BASE}/${workflowId}/schedule`),

  pauseSchedule: (workflowId: string) =>
    http().post<WorkflowSchedule>(`${BASE}/${workflowId}/schedule:pause`, {}),

  resumeSchedule: (workflowId: string) =>
    http().post<WorkflowSchedule>(`${BASE}/${workflowId}/schedule:resume`, {}),

  // ── Event rules ─────────────────────────────────────────────────

  listEventRules: (workflowId: string) =>
    http().get<EventRuleListResponse>(`${BASE}/${workflowId}/event-rules`),

  getEventRulesBatch: (workflowIds: string[]) =>
    http().get<BatchEventRuleResponse>(`${BASE}/event-rules/batch`, {
      params: { workflow_ids: workflowIds },
      paramsSerializer: { indexes: null },
    }),

  addEventRule: (workflowId: string, body: AddEventRuleRequest) =>
    http().post<EventRule>(`${BASE}/${workflowId}/event-rules`, body),

  deleteEventRule: (workflowId: string, ruleId: string) =>
    http().delete<void>(`${BASE}/${workflowId}/event-rules/${ruleId}`),

  // ── Run history ─────────────────────────────────────────────────

  listRuns: (workflowId: string, params?: { limit?: number; offset?: number }) =>
    http().get<WorkflowRunListResponse>(`${BASE}/${workflowId}/runs`, { params }),

  listAllRuns: (params?: {
    workflow_id?: string;
    session_id?: string;
    limit?: number;
    offset?: number;
  }) =>
    // Bounded wait for every runs-history consumer (History view, Work-home
    // banner, and the open-latest-run path once it lands). App-level query
    // retry (1) means the practical worst case is ~2x this.
    http().get<WorkflowRunListResponse>(`${BASE}/runs`, {
      params,
      timeout: 15_000,
    }),
};
